import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import FieldMappingForm from './FieldMappingForm';

const FieldMappingManagement = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [fieldMappings, setFieldMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewMode, setPreviewMode] = useState('variables'); // 'variables' or 'sample'
  const [templateSelectDialogOpen, setTemplateSelectDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [filters, setFilters] = useState({
    documentType: '',
    department: ''
  });

  const departments = [
    'OPERATIONS',
    'SALES',
    'MAINTENANCE',
    'SAFETY',
    'PLANNING',
    'ENGINEERING',
    'STORE',
    'GENERAL'
  ];

  const fetchFieldMappings = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.documentType) params.append('documentType', filters.documentType);
      if (filters.department) params.append('department', filters.department);

      const response = await axios.get(`/api/v1/pdf/field-mappings?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setFieldMappings(response.data.fieldMappings || []);
    } catch (error) {
      console.error('Error fetching field mappings:', error);
      showToast('Failed to fetch field mappings', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    fetchFieldMappings();
  }, [fetchFieldMappings]);

  const handleCreate = () => {
    setSelectedMapping(null);
    setFormOpen(true);
  };

  const handleCreateFromTemplate = () => {
    setTemplateSelectDialogOpen(true);
    fetchTemplates();
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await axios.get('/api/v1/pdf/templates', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast('Failed to fetch templates', 'error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = async (template) => {
    try {
      setLoadingTemplates(true);
      // Analyze the template
      const analyzeResponse = await axios.post(
        `/api/v1/pdf/templates/${template.templateId}/${template.version || ''}/analyze`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const analysis = analyzeResponse.data.analysis;
      
      // Create a pre-populated mapping from the analysis
      const suggestedMapping = {
        mappingId: `${template.templateId}-mapping`,
        name: `${template.name} Field Mapping`,
        description: `Auto-generated from template: ${template.name}`,
        documentType: template.documentType || '',
        department: template.department || '',
        templateId: template._id,
        version: '1.0.0',
        isDefault: false,
        dataSources: getPresetDataSources(template.documentType || ''),
        mappings: {},
        sections: analysis.sections || [],
        transformations: {},
        validationRules: {}
      };

      // Create basic mappings from suggested variables
      if (analysis.suggestedVariables) {
        analysis.suggestedVariables.forEach((variable, index) => {
          // Try to map to first data source
          if (suggestedMapping.dataSources.length > 0) {
            const sourceName = suggestedMapping.dataSources[0].name;
            suggestedMapping.mappings[variable] = {
              source: `${sourceName}.${variable}`,
              default: ''
            };
          } else {
            suggestedMapping.mappings[variable] = '';
          }
        });
      }

      setSelectedMapping(suggestedMapping);
      setTemplateSelectDialogOpen(false);
      setFormOpen(true);
      showToast('Template analyzed! Review and adjust the field mappings.', 'success');
    } catch (error) {
      console.error('Error analyzing template:', error);
      showToast('Failed to analyze template', 'error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Helper function to get preset data sources
  const getPresetDataSources = (documentType) => {
    const presets = {
      'fdr': [
        { name: 'flightLogEntry', model: 'FlightLogEntry', lookupField: '_id', populate: [] },
        { name: 'sector', model: 'Sector', lookupField: '_id', populate: ['aircraftId', 'assignedPilots', 'tripId'] },
        { name: 'aircraft', model: 'Aircraft', lookupField: '_id', populate: [] }
      ],
      'itinerary': [
        { name: 'trip', model: 'Trip', lookupField: '_id', populate: [] },
        { name: 'sector', model: 'Sector', lookupField: '_id', populate: ['aircraftId', 'assignedPilots'] }
      ],
      'maintenance-report': [
        { name: 'maintenance', model: 'Maintenance', lookupField: '_id', populate: ['aircraftId', 'assignedTo'] },
        { name: 'aircraft', model: 'Aircraft', lookupField: '_id', populate: [] }
      ],
      'duty-roster': [
        { name: 'dutyRosterEntry', model: 'DutyRosterEntry', lookupField: '_id', populate: ['crew'] }
      ]
    };
    return presets[documentType] || [];
  };

  const handleEdit = (mapping) => {
    setSelectedMapping(mapping);
    setFormOpen(true);
  };

  const handleCopy = (mapping) => {
    // Clone the mapping with a new ID and version
    const copiedMapping = {
      ...mapping,
      mappingId: `${mapping.mappingId}-copy-${Date.now()}`,
      name: `${mapping.name} (Copy)`,
      version: '1.0.0',
      isDefault: false,
      isActive: true,
      _id: undefined // Remove ID so it creates a new one
    };
    setSelectedMapping(copiedMapping);
    setFormOpen(true);
  };

  const handleView = (mapping) => {
    setSelectedMapping(mapping);
    setViewDialogOpen(true);
  };

  const handlePreviewPDF = (mapping) => {
    setSelectedMapping(mapping);
    setPreviewMode('variables'); // Start with variables tab
    updatePreviewUrl(mapping, 'variables');
    setPreviewDialogOpen(true);
  };

  const updatePreviewUrl = (mapping, mode) => {
    const token = localStorage.getItem('token');
    const version = mapping.version || '';
    const url = `${window.location.origin}/api/v1/pdf/field-mappings/${mapping.mappingId}/${version}/preview?token=${encodeURIComponent(token)}&mode=${mode}`;
    setPreviewUrl(url);
  };

  const handlePreviewTabChange = (event, newValue) => {
    setPreviewMode(newValue);
    if (selectedMapping) {
      updatePreviewUrl(selectedMapping, newValue);
    }
  };

  const handleDelete = async (mapping) => {
    if (!window.confirm(`Are you sure you want to delete "${mapping.name}"?`)) {
      return;
    }

    try {
      // Note: Delete endpoint would need to be implemented
      showToast('Delete functionality to be implemented', 'info');
    } catch (error) {
      showToast('Failed to delete field mapping', 'error');
    }
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    fetchFieldMappings();
  };

  const canManage = user?.role === ROLES.SPECTATOR;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Field Mapping Management</Typography>
        {canManage && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateFromTemplate}
            >
              Create from Template
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Create New
            </Button>
          </Stack>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={filters.documentType}
                onChange={(e) => setFilters({ ...filters, documentType: e.target.value })}
                label="Document Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="itinerary">Itinerary</MenuItem>
                <MenuItem value="fdr">FDR</MenuItem>
                <MenuItem value="maintenance-report">Maintenance Report</MenuItem>
                <MenuItem value="duty-roster">Duty Roster</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="">All</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept} value={dept}>
                    {dept.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Field Mappings Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Mapping ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : fieldMappings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No field mappings found
                </TableCell>
              </TableRow>
            ) : (
              fieldMappings.map((mapping) => (
                <TableRow key={mapping._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {mapping.mappingId}
                    </Typography>
                  </TableCell>
                  <TableCell>{mapping.name}</TableCell>
                  <TableCell>
                    <Chip label={mapping.documentType} size="small" />
                  </TableCell>
                  <TableCell>{mapping.department.replace(/_/g, ' ')}</TableCell>
                  <TableCell>
                    <Chip label={mapping.version} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={mapping.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={mapping.isActive ? 'success' : 'default'}
                    />
                    {mapping.isDefault && (
                      <Chip label="Default" size="small" color="secondary" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleView(mapping)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Preview PDF">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handlePreviewPDF(mapping)}
                      >
                        <PdfIcon />
                      </IconButton>
                    </Tooltip>
                    {canManage && (
                      <>
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleCopy(mapping)}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(mapping)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(mapping)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Form Dialog */}
      <FieldMappingForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
        existingMapping={selectedMapping}
      />

      {/* Template Selection Dialog */}
      <Dialog
        open={templateSelectDialogOpen}
        onClose={() => setTemplateSelectDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Template to Analyze</DialogTitle>
        <DialogContent>
          {loadingTemplates ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>Loading templates...</Typography>
            </Box>
          ) : templates.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No templates found. Please create a template first.
            </Alert>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Template ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Document Type</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template._id}>
                      <TableCell>{template.templateId}</TableCell>
                      <TableCell>{template.name}</TableCell>
                      <TableCell>
                        <Chip label={template.documentType} size="small" />
                      </TableCell>
                      <TableCell>{template.department?.replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        <Chip label={template.version} size="small" color="primary" />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleTemplateSelect(template)}
                        >
                          Use This Template
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateSelectDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Field Mapping Details</DialogTitle>
        <DialogContent>
          {selectedMapping && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2">Mapping ID</Typography>
                <Typography variant="body1">{selectedMapping.mappingId}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Name</Typography>
                <Typography variant="body1">{selectedMapping.name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Description</Typography>
                <Typography variant="body1">{selectedMapping.description || 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Data Sources</Typography>
                <Typography variant="body2" component="pre" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                  {JSON.stringify(selectedMapping.dataSources, null, 2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Field Mappings</Typography>
                <Typography variant="body2" component="pre" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                  {JSON.stringify(selectedMapping.mappings, null, 2)}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Preview PDF Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6">
              Preview PDF - {selectedMapping?.name || 'Field Mapping Preview'}
            </Typography>
            <Tabs
              value={previewMode}
              onChange={handlePreviewTabChange}
              sx={{ mt: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Template Variables" value="variables" />
              <Tab label="Preview PDF with Sample Data" value="sample" />
            </Tabs>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, position: 'relative' }}>
            <iframe
              key={previewUrl} // Force reload when URL changes
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title="PDF Preview"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldMappingManagement;

